import {
  addDays,
  differenceInDays,
  formatISO,
  parseISO,
  startOfDay,
} from 'date-fns'
import { default as GraphemeSplitter } from 'grapheme-splitter'
import queryString from 'query-string'

import { ENABLE_ARCHIVED_GAMES } from '../constants/settings'
import { NOT_CONTAINED_MESSAGE, WRONG_SPOT_MESSAGE } from '../constants/strings'
import { VALID_GUESSES } from '../constants/validGuesses'
import {
  DAILY_WORDS,
  FRESHMAN,
  JUNIOR,
  SENIOR,
  SOPHOMORE,
  WORDS,
} from '../constants/wordlist'
import { TEACHER_WORDS, TEACHER_WORDS_FULL } from '../teacherWords'
import { getToday } from './dateutils'
import { getGuessStatuses } from './statuses'

// 1 January 2022 Game Epoch

export const firstGameDate = new Date(2023, 2, 1)
export const periodInDays = 1

export const isWordInWordList = (word: string, allowedWords?: string[]) => {
  const lower = localeAwareLowerCase(word)

  if (allowedWords) {
    return allowedWords.includes(lower)
  }

  return (
    WORDS.includes(lower) ||
    VALID_GUESSES.includes(lower) ||
    TEACHER_WORDS.includes(lower) ||
    TEACHER_WORDS_FULL.includes(lower) ||
    SENIOR.includes(lower) ||
    JUNIOR.includes(lower) ||
    SOPHOMORE.includes(lower) ||
    FRESHMAN.includes(lower)
  )
}

export const isWinningWord = (word: string) => {
  return solution === word
}

// build a set of previously revealed letters - present and correct
// guess must use correct letters in that space and any other revealed letters
// also check if all revealed instances of a letter are used (i.e. two C's)
export const findFirstUnusedReveal = (word: string, guesses: string[]) => {
  if (guesses.length === 0) {
    return false
  }

  const lettersLeftArray = new Array<string>()
  const guess = guesses[guesses.length - 1]
  const statuses = getGuessStatuses(solution, guess)
  const splitWord = unicodeSplit(word)
  const splitGuess = unicodeSplit(guess)

  for (let i = 0; i < splitGuess.length; i++) {
    if (statuses[i] === 'correct' || statuses[i] === 'present') {
      lettersLeftArray.push(splitGuess[i])
    }
    if (statuses[i] === 'correct' && splitWord[i] !== splitGuess[i]) {
      return WRONG_SPOT_MESSAGE(splitGuess[i], i + 1)
    }
  }

  // check for the first unused letter, taking duplicate letters
  // into account - see issue #198
  let n
  for (const letter of splitWord) {
    n = lettersLeftArray.indexOf(letter)
    if (n !== -1) {
      lettersLeftArray.splice(n, 1)
    }
  }

  if (lettersLeftArray.length > 0) {
    return NOT_CONTAINED_MESSAGE(lettersLeftArray[0])
  }
  return false
}

export const unicodeSplit = (word: string) => {
  return new GraphemeSplitter().splitGraphemes(word)
}

export const unicodeLength = (word: string) => {
  return unicodeSplit(word).length
}

export const localeAwareLowerCase = (text: string) => {
  return process.env.REACT_APP_LOCALE_STRING
    ? text.toLocaleLowerCase(process.env.REACT_APP_LOCALE_STRING)
    : text.toLowerCase()
}

export const localeAwareUpperCase = (text: string) => {
  return process.env.REACT_APP_LOCALE_STRING
    ? text.toLocaleUpperCase(process.env.REACT_APP_LOCALE_STRING)
    : text.toUpperCase()
}

export const getLastGameDate = (today: Date) => {
  const t = startOfDay(today)
  let daysSinceLastGame = differenceInDays(firstGameDate, t) % periodInDays
  return addDays(t, -daysSinceLastGame)
}

export const getNextGameDate = (today: Date) => {
  return addDays(getLastGameDate(today), periodInDays)
}

export const isValidGameDate = (date: Date) => {
  if (date < firstGameDate || date > getToday()) {
    return false
  }

  return differenceInDays(firstGameDate, date) % periodInDays === 0
}

export const getIndex = (gameDate: Date) => {
  let start = firstGameDate
  let index = -1
  do {
    index++
    start = addDays(start, periodInDays)
  } while (start <= gameDate)

  return index
}

export const getWordOfDay = (index: number) => {
  if (index < 0) {
    throw new Error('Invalid index')
  }

  // Use DAILY_WORDS (names ≥ 4 letters) so short names like "jb" or "rio" never
  // become the daily answer. WORDS is still used for full guess validation.
  const pool = DAILY_WORDS.length > 0 ? DAILY_WORDS : WORDS
  return localeAwareUpperCase(pool[index % pool.length])
}

export const getSolution = (gameDate: Date) => {
  const nextGameDate = getNextGameDate(gameDate)
  const index = getIndex(gameDate)
  const wordOfTheDay = getWordOfDay(index)

  return {
    solution: wordOfTheDay,
    solutionGameDate: gameDate,
    solutionIndex: index,
    tomorrow: nextGameDate.valueOf(),
  }
}

// Grade-aware solution lookup for leaderboard validation.
// Returns the expected daily word for a specific grade on a given date,
// using that grade's actual word list — not the current user's list.
export const getSolutionForGrade = (
  gameDate: Date,
  gradeNum: number
): string => {
  const idx = getIndex(gameDate)
  if (gradeNum === 0) {
    // Teachers use the same offset formula as the runtime teacher round
    const offset = Math.floor(TEACHER_WORDS.length / 2)
    return localeAwareUpperCase(
      TEACHER_WORDS[(idx + offset) % TEACHER_WORDS.length]
    )
  }
  const gradeLists: Record<number, string[]> = {
    9: FRESHMAN,
    10: SOPHOMORE,
    11: JUNIOR,
    12: SENIOR,
  }
  const list = gradeLists[gradeNum] ?? JUNIOR
  return localeAwareUpperCase(list[idx % list.length])
}

export default getSolution

export const getGameDate = () => {
  if (getIsLatestGame()) {
    return getToday()
  }

  const parsed = queryString.parse(window.location.search)
  try {
    const d = startOfDay(parseISO(parsed.d!.toString()))
    if (d >= getToday() || d < firstGameDate) {
      setGameDate(getToday())
    }
    return d
  } catch (e) {
    return getToday()
  }
}

export const setGameDate = (d: Date) => {
  try {
    if (d < getToday()) {
      window.location.href = '/?d=' + formatISO(d, { representation: 'date' })
      return
    }
  } catch (e) {}
  window.location.href = '/'
}

export const getIsLatestGame = () => {
  if (!ENABLE_ARCHIVED_GAMES) {
    return true
  }
  const parsed = queryString.parse(window.location.search)
  return parsed === null || !('d' in parsed)
}

export const { solution, solutionGameDate, solutionIndex, tomorrow } =
  getSolution(getGameDate())
